import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DateService } from '@edtech-ui/core/services/dateService/date.service';
import { AdminStudentPlatformHeaderComponent } from '../admin-student-platform-header/admin-student-platform-header.component';
import { AdminStudentPlatformService } from '@edtech-ui/core/services/admin-student-platform.service';
import { userManagementGetUsersEnum } from '@edtech-ui/constants/user-managemnt-get-users-status.enum';

@Component({
  selector: 'app-dialog-user-details',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminStudentPlatformHeaderComponent],
  templateUrl: './dialog-user-details.component.html',
  styleUrl: './dialog-user-details.component.scss'
})
export class DialogUserDetailsComponent implements OnInit {

  dateFormat = DateService.APP_DATE_TIME_SHORT;
  dateTimeFormat = DateService.APP_DATE_TIME_BOTH;

  @Input() user: any = null;
  @Output() close = new EventEmitter<void>();

  userId: string | null = null;
  isLoading = false;

  activeDetailTab: 'overview' | 'job_applications' = 'overview';
  jobApplications: any[] = [];
  isLoadingJobApplications = false;
  jobSearchQuery: string = '';
  jobCurrentPage: number = 1;
  jobPageSize: number = 5;

  get filteredJobApplications(): any[] {
    if (!this.jobSearchQuery || !this.jobSearchQuery.trim()) {
      return this.jobApplications;
    }
    const query = this.jobSearchQuery.trim().toLowerCase();
    return this.jobApplications.filter(app =>
      app.job_title && app.job_title.toLowerCase().includes(query)
    );
  }

  get paginatedJobApplications(): any[] {
    const start = (this.jobCurrentPage - 1) * this.jobPageSize;
    return this.filteredJobApplications.slice(start, start + this.jobPageSize);
  }

  get totalJobPages(): number {
    return Math.ceil(this.filteredJobApplications.length / this.jobPageSize) || 1;
  }

  changeJobPage(page: number): void {
    if (page >= 1 && page <= this.totalJobPages) {
      this.jobCurrentPage = page;
    }
  }

  getJobEndIndex(): number {
    return Math.min(this.jobCurrentPage * this.jobPageSize, this.filteredJobApplications.length);
  }

  clearJobSearch(): void {
    this.jobSearchQuery = '';
    this.jobCurrentPage = 1;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminStudentPlatformService
  ) {}

  ngOnInit(): void {
    const navState = history.state;
    if (navState && navState.user) {
      this.user = navState.user;
    }

    this.route.paramMap.subscribe(params => {
      this.userId = params.get('userId');
      if (!this.user && this.userId) {
        this.fetchUserDetails(this.userId);
      }
      if (this.userId) {
        this.fetchJobApplications(this.userId);
      } else {
        // Fallback fetch job applications
        this.fetchJobApplications('default');
      }
    });
  }

  fetchJobApplications(userId: string) {
    this.isLoadingJobApplications = true;
    this.adminService.getUserJobApplications(userId).subscribe({
      next: (apps) => {
        this.jobApplications = apps || [];
        this.isLoadingJobApplications = false;
      },
      error: () => {
        this.isLoadingJobApplications = false;
      }
    });
  }

  fetchUserDetails(userId: string) {
    this.isLoading = true;
    this.adminService.getUsers(1, 1, userId, userManagementGetUsersEnum.ALL, 'all').subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && res.users && res.users.length > 0) {
          const u = res.users[0];
          this.user = {
            name: u.name,
            email: u.email,
            registered: u.registered,
            phone_number: u.phone_number,
            college_name: u.college_name,
            city: u.city,
            pincode: u.pincode,
            enrolledCourses: u.enrolled_courses,
            quizAttempts: u.quiz_attempts,
            ai_interview_count: u.ai_interview_count,
            ai_mock_test_count: u.ai_mock_test_count,
            paymentStatus: u.payment_status,
            is_active: u.is_active,
            registration_source: u.registration_source,
            last_login_source: u.last_login_source,
            last_login_at: u.last_login_at,
            mentor_sessions_booked: u.mentor_sessions_booked,
            job_applied_count: u.job_applied_count ?? (u as any).jobs_applied_count ?? 0
          };
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  closeModal() {
    if (this.close.observed) {
      this.close.emit();
    } else {
      this.router.navigate(['/admin-student-platform/users']);
    }
  }

  goBack(): void {
    this.router.navigate(['/admin-student-platform/users']);
  }

  getPaymentClass(status?: string): string {
    if (!status) return 'badge-default';

    switch (status.toLowerCase()) {
      case 'paid':
        return 'badge-paid';
      case 'pending':
        return 'badge-pending';
      case 'failed':
        return 'badge-failed';
      default:
        return 'badge-default';
    }
  }

  getInitials(name?: string): string {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  }

  getFormattedJobStatus(app: any): string {
    if (!app) return 'Applied';
    const status = app.current_status || app.status || 'Applied';

    const intStatus = (app.interview_status || app.interview?.status || '').toString().toLowerCase();
    const assScore = app.assessment_score || app.assessment?.score;

    // Fallback for legacy records stored with generic "Applied" status
    if (status.toLowerCase() === 'applied') {
      if (['completed', 'pass', 'passed'].includes(intStatus)) {
        return 'Interview Completed';
      }
      if (['scheduled', 'invited', 'pending'].includes(intStatus)) {
        return 'Interview Scheduled';
      }
      if (assScore !== undefined && assScore !== null && assScore !== '--' && assScore !== 'Pending' && assScore !== 'pending') {
        return 'Assessment Completed';
      }
      if (assScore === 'Pending' || assScore === 'pending' || app.assessment) {
        return 'Assessment Pending';
      }
    }

    return status;
  }

  getStatusBadgeClass(status?: string): string {
    if (!status) return 'status-blue';
    const s = status.toLowerCase();
    if (s.includes('pending')) return 'status-orange';
    if (s.includes('completed')) return 'status-purple';
    if (s.includes('interview') || s.includes('scheduled')) return 'status-indigo';
    if (s.includes('rejected') || s.includes('failed')) return 'status-red';
    if (s.includes('hired') || s.includes('passed')) return 'status-green';
    return 'status-blue';
  }
}
